import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Paper, Box, Grid, Card, CardContent, Button } from "@mui/material";
import BarChart from "../../components/Charts/BarChart.js";
import TitledSelector from "../../components/Selectors/TitledSelector.js";
import DateSelector from "../../components/Selectors/DateSelector.js";
import InfoCard from "../../components/Cards/InfoCard.js";
import TextDate from "../../components/Texts/DateText.js";
import ApplyButton from "../../components/Buttons/ApplyButton.js";
import LoadingSpinner from "../../components/Loaders/SpinLoader.js";
import * as styles from "../Dashboard/Styles.js";
import { Spaces, mainBox } from "../../assets/Variables/SpaceVariables.js";
import Colors from "../../assets/Variables/ColorsVariables.js";
import * as TextVariables from "../../assets/Variables/TextVariables";
import { useCookies } from "../../Tools/CookieChecker.js";
import ClientInfoRequest from "../../Endpoints/ClientInfoRequest.js";
import {
  SetDashboardData,
  GetDashboardData,
  RemoveDashboardData,
  SetDashboardSelectors,
  GetDashboardSelectors
} from "../../Tools/LocalStorage.js";
import { getLocalTimeZoneOffsetInSeconds, getTimestamps } from "../../Tools/DateTimeConvertions";
import dayjs from "dayjs";
import WalletRequest from "../../Endpoints/WalletRequest.js";
import TitledMultiSelector from "../../components/Selectors/TitledMultiSelector.js";
import { BalanceFormatter, CurrencySymbol } from "../../components/CurrencyIcon/CurrencyIcon.js";

function DashboardIndexPage(props) {
  const { type, currency } = useCookies();
  const [dateSelect, setDateSelect] = useState("Today");
  const [brokerSelect, setBrokerSelect] = useState(["All"]);
  const [showDataPickers, setShowDataPickers] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState({
    total_accounts: 0,
    new_accounts: 0,
    total_volume: 0,
    total_deposits: 0,
    total_withdraws: 0,
    total_commissions: 0,
    commissions_by_currency: []
  });
  const [ibs, setIbs] = useState([{ label: "All", value: "All" }]);
  const [wallet, setWallet] = useState({});
  const [loading, setLoading] = useState(false);
  const [applyDisable, setApplyDisable] = useState(false);
  const [barData, setBarData] = useState({
    labels: [],
    datasets: [
      {
        axis: "y",
        label: ["My First Dataset"],
        data: [0, 0, 0],
        fill: false,
        backgroundColor: [
          Colors.Secondary_900,
          Colors.Info_500,
          Colors.Primary_400
        ],
        borderColor: [
          Colors.Secondary_900,
          Colors.Info_500,
          Colors.Primary_400
        ],
        borderWidth: 1
      }
    ]
  });

  const [chartData, setChartData] = React.useState({
    labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
    datasets: [
      {
        label: "Volume",
        data: [65, 59, 80, 81],
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        yAxisID: "y-axis-volumen",
        tension: 0.4
      },
      {
        label: "Payouts",
        data: [28, 48, 40, 19],
        borderColor: "rgba(153, 102, 255, 1)",
        backgroundColor: "rgba(153, 102, 255, 0.2)",
        yAxisID: "y-axis-dinero",
        tension: 0.4
      },
      {
        label: "Deposits",
        data: [35, 29, 50, 71],
        borderColor: "rgba(255, 159, 64, 1)",
        backgroundColor: "rgba(255, 159, 64, 0.2)",
        yAxisID: "y-axis-dinero",
        tension: 0.4
      }
    ]
  });

  // Número de monedas en commissions_by_currency (0, 1 o más)
  const commissionsCount = dashboard?.commissions_by_currency?.length || 0;

  useEffect(() => {
    RequestWalletData();
    if (!GetLocalStorageData()) {
      let requestData = getTimestamps("Today");
      RequestDashboardData(requestData);
      setDateSelect("Today");
      setFromDate(null);
      setToDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const RequestWalletData = async () => {
    try {
      const response = await WalletRequest();
      if (response.data != null) {
        setWallet(response.data.data.wallet);
      } else {
        setWallet({ id: "", currency: "USD", balance: 0 });
      }
    } catch (error) {
      setWallet({ id: "", currency: "USD", balance: 0 });
      console.log(error);
    }
  };

  const GetLocalStorageData = () => {
    let dataSelector = GetDashboardSelectors();
    if (dataSelector != null) {
      if (dataSelector.dateSelector === "Custom") setShowDataPickers(true);
      setDateSelect(dataSelector.dateSelector);
      setFromDate(dayjs(dataSelector.fromDate));
      setToDate(dayjs(dataSelector.toDate));
      setBrokerSelect(dataSelector.ib);
    } else {
      setDateSelect("Today");
      setFromDate(null);
      setToDate(null);
      setBrokerSelect(["All"]);
    }

    let dataResponse = GetDashboardData();
    if (dataResponse.dataExits) {
      setDashboard(dataResponse.dashboard);
      const formattedData = dataResponse.ibs.map((item) => ({
        label: item.name,
        value: item.email_ib
      }));

      setIbs([{ label: "All", value: "All" }, ...formattedData]);

      setBarData((prevBarData) => ({
        ...prevBarData,
        labels: [
          `Active accounts (${dataResponse.dashboard.total_active_accounts})`,
          `Inactive accounts (${dataResponse.dashboard.total_inactive_accounts})`,
          `Unfunded accounts (${dataResponse.dashboard.total_unfunded_accounts})`
        ],
        datasets: [
          {
            ...prevBarData.datasets[0],
            data: [
              dataResponse.dashboard.total_active_accounts,
              dataResponse.dashboard.total_inactive_accounts,
              dataResponse.dashboard.total_unfunded_accounts
            ]
          }
        ]
      }));
      return true;
    } else {
      return false;
    }
  };

  const RequestDashboardData = async (requestData) => {
    try {
      setLoading(true);
      setApplyDisable(true);

      if (brokerSelect && brokerSelect.length > 0) {
        if (brokerSelect[0] !== "All") {
          requestData.ib_email = brokerSelect;
        }
      }

      const response = await ClientInfoRequest(requestData);
      if (response.status === 200) {
        SetDashboardData(response);

        const d = response.data.data.dashboard;

        setDashboard({
          total_accounts: d.total_accounts,
          new_accounts: d.new_accounts,
          total_volume: d.total_volume,
          total_deposits: d.total_deposits,
          total_withdraws: d.total_withdraws,
          total_commissions: d.total_commissions,
          commissions_by_currency: d.commissions_by_currency || []
        });

        setBarData((prevBarData) => ({
          ...prevBarData,
          labels: [
            `Active accounts (${d.total_active_accounts})`,
            `Inactive accounts (${d.total_inactive_accounts})`,
            `Unfunded accounts (${d.total_unfunded_accounts})`
          ],
          datasets: [
            {
              ...prevBarData.datasets[0],
              data: [
                d.total_active_accounts,
                d.total_inactive_accounts,
                d.total_unfunded_accounts
              ]
            }
          ]
        }));

        if (response.data.data.role === "BROKER") {
          const formattedBrokers = response.data.data.ibs.map((item) => ({
            label: item.name,
            value: item.email_ib
          }));

          const allOptions = formattedBrokers.map((item) => item.value);

          setIbs([{ label: "All", value: "All" }, ...formattedBrokers]);

          if (brokerSelect && brokerSelect.length > 0 && brokerSelect[0] === "All") {
            SetDashboardSelectors({
              dateSelector: dateSelect,
              fromDate: fromDate,
              toDate: toDate,
              ib: ["All", ...allOptions]
            });
            handleBrokerIbSelector(["All", ...allOptions]);
          } else {
            SetDashboardSelectors({
              dateSelector: dateSelect,
              fromDate: fromDate,
              toDate: toDate,
              ib: brokerSelect
            });
          }
        } else {
          SetDashboardSelectors({
            dateSelector: dateSelect,
            fromDate: fromDate,
            toDate: toDate,
            ib: null
          });
        }

        setLoading(false);
      } else {
        RemoveDashboardData();
        setDashboard({});
        SetDashboardSelectors({});
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      RemoveDashboardData();
      setDashboard({});
      SetDashboardSelectors({});
      setLoading(false);
    }
    setLoading(false);
    setApplyDisable(false);
  };

  const handleDateSelector = (event) => {
    setDateSelect(event.target.value);
    if (event.target.value === "Custom") {
      setShowDataPickers(true);
    } else {
      setShowDataPickers(false);
    }
  };

  const handleBrokerIbSelector = (selectedValues) => {
    const allOptions = ibs.map((item) => item.value);

    const isAllSelected = selectedValues.includes("All");
    const hadAllBefore = brokerSelect.includes("All");

    if (isAllSelected && !hadAllBefore) {
      setBrokerSelect(allOptions);
      return;
    }

    if (!isAllSelected && hadAllBefore) {
      setBrokerSelect([]);
      return;
    }
    if (hadAllBefore && selectedValues.length < allOptions.length) {
      setBrokerSelect(selectedValues.filter((val) => val !== "All"));
      return;
    }

    const selectedWithoutAll = selectedValues.filter((val) => val !== "All");
    const allExceptAll = allOptions.filter((val) => val !== "All");

    const selectedAllManually =
      selectedWithoutAll.length === allExceptAll.length &&
      allExceptAll.every((val) => selectedWithoutAll.includes(val));

    if (selectedAllManually) {
      setBrokerSelect(allOptions);
      return;
    }

    setBrokerSelect(selectedValues);
  };

  const handleFromChange = (date) => {
    setFromDate(date);
  };

  const handleToChange = (date) => {
    setToDate(date);
  };

  const handleSeeWalletdetails = () => {
    navigate("/my-wallet");
  };

  const HandleOnClick = () => {
    if (dateSelect !== "Custom") {
      let datetime = getTimestamps(dateSelect);
      RequestDashboardData({
        from: datetime.from,
        to: datetime.to
      });
      SetDashboardSelectors({
        dateSelector: dateSelect,
        fromDate: null,
        toDate: null,
        ib: brokerSelect
      });
    } else {
      if (fromDate != null || toDate != null) {
        RequestDashboardData({
          from: fromDate / 1000 + getLocalTimeZoneOffsetInSeconds(),
          to: toDate / 1000 + getLocalTimeZoneOffsetInSeconds() + 86400
        });
        SetDashboardSelectors({
          dateSelector: dateSelect,
          fromDate: fromDate,
          toDate: toDate,
          ib: brokerSelect
        });
      }
    }

    RequestWalletData();
  };

  const dateOptions = [
    { label: "Today", value: "Today" },
    { label: "This week", value: "This week" },
    { label: "This month", value: "This month" },
    { label: "This year", value: "This year" },
    { label: "Custom", value: "Custom" }
  ];

  const dataGrid = [
    {
      title: "Total accounts",
      value: `${dashboard.total_accounts}`,
      icon: "group",
      toolText: ""
    },
    {
      title: "New accounts",
      value: dashboard.new_accounts,
      icon: "person_add",
      toolText: "The amount of accounts opened during the selected period of time."
    },
    {
      title: "Volume (Lots)",
      value: (dashboard.total_volume || 0).toFixed(4),
      icon: "candlestick_chart",
      toolText: ""
    },
    {
      title: `Deposits (${wallet.currency || currency})`,
      value: `${CurrencySymbol.get(wallet.currency || currency)}${BalanceFormatter.round((dashboard.total_deposits || 0), (wallet.currency || currency))}`,
      icon: "add_card",
      toolText: ""
    },
    {
      title: `Withdrawals (${wallet.currency || currency})`,
      value: `${CurrencySymbol.get(wallet.currency || currency)}${BalanceFormatter.round((dashboard.total_withdraws || 0), (wallet.currency || currency))}`,
      icon: "payment_arrow_down",
      toolText: ""
    },
    {
      title: `Payouts (${wallet.currency || currency})`,
      value: `${CurrencySymbol.get(wallet.currency || currency)}${BalanceFormatter.round((dashboard.total_commissions || 0), (wallet.currency || currency))}`,
      icon: "trending_up",
      toolText: ""  
    },
  ];

  return (
    <Box sx={mainBox}>
      {loading ? <LoadingSpinner /> : null}
      <Paper elevation={0} sx={styles.PaperStyle}>
        <Box sx={styles.TitleBox}>
          <Typography gutterBottom fontFamily={TextVariables.Body_3_bold}>
            Dashboard
          </Typography>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            fontFamily={TextVariables.Body_4}
          >
            Track your clients' performance and your results over time.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: 2,
            width: "100%",
          }}
        >
          <TitledSelector
            title="Period of time"
            value={dateSelect}
            handleChange={handleDateSelector}
            options={dateOptions}
          />
          {showDataPickers && (
            <>
              <DateSelector
                title="From"
                value={fromDate}
                handleChange={handleFromChange}
              />
              <DateSelector
                title="To"
                value={toDate}
                handleChange={handleToChange}
              />
            </>
          )}
          {(type === "BROKER" || type === "MASTER_IB") ? (
            <TitledMultiSelector
              title="IB selector"
              value={brokerSelect}
              handleChange={handleBrokerIbSelector}
              options={ibs}
              tooltext="Select a Broker or IB linked to you through trees (payout structures) to see their general performance overview."
            />
          ) : null}

          <ApplyButton
            HandleOnClick={HandleOnClick}
            disable={applyDisable || (brokerSelect && brokerSelect.length === 0)}
          >
            Apply
          </ApplyButton>
        </Box>

        <Box margin={`${Spaces.Size_7} 0 0 0`}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={9} container spacing={3}>
              {commissionsCount > 1
                ? dataGrid.map((item, index) => (
                    <Grid
                      key={index}
                      item
                      xs={12}
                      sm={index <= 2 ? 4 : index <= 4 ? 6 : 12}
                      md={index <= 2 ? 4 : index <= 4 ? 6 : 12}
                    >
                      <InfoCard
                        title={item.title}
                        value={
                          item.title?.toLowerCase().includes("payouts") ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "24px",
                                alignItems: "center",
                                flexWrap: "wrap"
                              }}
                            >
                              {dashboard.commissions_by_currency.map((el, i) => (
                                <div key={i} style={{ whiteSpace: "nowrap" }}>
                                  {el.currency}: $
                                  {Number(el.amount || 0).toFixed(2)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            item.value ?? 0
                          )
                        }
                        icon={item.icon}
                        tooltext={item.toolText}
                      />
                    </Grid>
                  ))
                : dataGrid.map((item, index) => (
                    <Grid key={index} item xs={12} sm={6} md={4}>
                      <InfoCard
                        title={item.title}
                        value={
                          item.title?.toLowerCase().includes("payouts") &&
                          commissionsCount === 1 ? (
                            dashboard.commissions_by_currency.map((el, i) => (
                              <div key={i} style={{ whiteSpace: "nowrap" }}>
                                {CurrencySymbol.get(el.currency || currency)}{BalanceFormatter.round((el.amount || 0), (wallet.currency || currency))}
                              </div>
                            ))
                          ) : (
                            item.value ?? 0
                          )
                        }
                        icon={item.icon}
                        tooltext={item.toolText}
                      />
                    </Grid>
                  ))}
            </Grid>

            <Grid item xs={12} md={3}>
              <Card style={styles.BalanceCardStyle}>
                <CardContent style={styles.BalanceCardContentStyle}>
                  <Box>
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "30px" }}
                    >
                      account_balance_wallet
                    </span>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection={"column"}
                    alignItems={"center"}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{ fontFamily: TextVariables.Caption_1 }}
                    >
                      My wallet
                    </Typography>
                    <Typography
                      variant="h5"
                      component="div"
                      sx={{ fontFamily: TextVariables.Body_3_bold }}
                    >
                      {CurrencySymbol.get(wallet.currency || currency)}
                      {BalanceFormatter.round((wallet.balance || 0), (wallet.currency || currency))}
                    </Typography>
                  </Box>
                  <Button
                    onClick={handleSeeWalletdetails}
                    sx={{
                      height: 33,
                      width: 89,
                      color: "black",
                      fontFamily: TextVariables.Caption_bold_1,
                      textTransform: "none",
                      borderRadius: Spaces.Size_2,
                      padding: Spaces.Size_2,
                      gap: 8,
                      "&:hover": {
                        border: "1px solid black"
                      }
                    }}
                  >
                    See details
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Paper elevation={0} sx={styles.PaperChildrenStyle}>
          <Box sx={styles.AccountStatisticalBoxStyle}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                textAlign: "left",
                marginRight: "20px",
                gap: Spaces.Size_3
              }}
            >
              <Typography sx={TextVariables.Body_3_bold}>
                Monthly active account
              </Typography>
              <Typography sx={TextVariables.Body_4}>
                Keep updated on the general status of your accounts.
              </Typography>
              <TextDate icon={"calendar_today"} text={"June, 2024"}></TextDate>
            </Box>
            <Paper sx={styles.AccountsChartPaperStyle}>
              <BarChart dataChart={barData} />
            </Paper>
          </Box>
        </Paper>

        {/*  
        <Paper elevation={0} sx={styles.PaperPerformance}>
          <Box
            sx={{
              height: "100%",
              width: "100%",
              padding: Spaces.Size_7,
              transform: "scale(1)",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: Spaces.Size_7
            }}
          >
            <Box>
              <Typography gutterBottom fontFamily={TextVariables.Body_3_bold}>
                Perfomance Metrics
              </Typography>
              <TextDate icon={"calendar_today"} text={"June, 2024"}></TextDate>
            </Box>

            <LinesChart data={chartData} />
          </Box>
        </Paper>
        */}

        <Box sx={{ marginBottom: "100px" }}></Box>
      </Paper>
    </Box>
  );
}

export default DashboardIndexPage;
